/* ==========================================================================
   Milos Atelier — theme scripts
   ========================================================================== */
(() => {
  'use strict';

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const root = document.documentElement;
  const theme = window.theme || { routes: {}, strings: {} };

  const debounce = (fn, wait = 150) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const fetchHTML = async (url) => {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(String(response.status));
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  /* ---------- Money ---------- */
  function formatMoney(cents, format = theme.moneyFormat || '{{amount}}') {
    const value = Number(cents) || 0;
    const build = (precision, thousands = ',', decimal = '.') => {
      const [integer, decimals] = (value / 100).toFixed(precision).split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? grouped + decimal + decimals : grouped;
    };
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    let amount;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': amount = build(0); break;
      case 'amount_with_comma_separator': amount = build(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = build(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': amount = build(2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': amount = build(0, ' '); break;
      case 'amount_with_space_separator': amount = build(2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': amount = build(2, ' ', '.'); break;
      default: amount = build(2);
    }
    if (theme.hideDecimals) amount = amount.replace(/[.,]00$/, '');
    return format.replace(placeholder, amount);
  }

  /* ==========================================================================
     Header: measurements, sticky behaviour, transparent state
     ========================================================================== */
  const Header = {
    bound: false,

    refresh() {
      this.section = $('.section-header');
      this.header = $('[data-header]');
      this.announcement = $('.section-announcement');
      if (!this.header) return;
      this.sticky = this.header.dataset.sticky;
      this.transparent = this.header.classList.contains('header--transparent');
      this.lastY = window.scrollY;
      this.measure();
      this.onScroll();

      if (this.transparent) {
        this.header.addEventListener('mouseenter', () => this.header.classList.add('is-solid'));
        this.header.addEventListener('mouseleave', () => this.onScroll());
      }

      if (!this.bound) {
        this.bound = true;
        window.addEventListener('resize', debounce(() => this.measure(), 120));
        window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      }
    },

    measure() {
      if (!this.header) return;
      root.style.setProperty('--header-total', `${this.header.offsetHeight}px`);
      root.style.setProperty('--announcement-height', `${this.announcement ? this.announcement.offsetHeight : 0}px`);
    },

    onScroll() {
      if (!this.header) return;
      const y = Math.max(window.scrollY, 0);
      const headerHeight = this.header.offsetHeight;

      if (this.transparent) {
        this.header.classList.toggle('is-solid', y > 20 || this.header.matches(':hover'));
      }

      let offset = this.sticky === 'none' ? 0 : headerHeight;

      if (this.sticky === 'scroll-up' && this.section) {
        const locked = document.body.classList.contains('is-locked');
        const threshold = headerHeight + (this.announcement ? this.announcement.offsetHeight : 0);
        if (Math.abs(y - this.lastY) > 4 && !locked) {
          this.section.classList.toggle('is-hidden', y > this.lastY && y > threshold);
        }
        if (y <= threshold) this.section.classList.remove('is-hidden');
        if (this.section.classList.contains('is-hidden')) offset = 0;
      }

      root.style.setProperty('--header-offset', `${offset}px`);
      this.lastY = y;
    }
  };

  /* ==========================================================================
     Drawers (menu, search, cart, filters, size guide)
     ========================================================================== */
  const Drawer = {
    active: null,
    trigger: null,

    /* Drawers rendered inside the sticky header are moved to <body> so they
       stack above the page overlay. */
    hoist() {
      $$('.section-header [data-drawer]').forEach((drawer) => {
        $$(`body > [data-drawer="${drawer.dataset.drawer}"]`).forEach((old) => old.remove());
        drawer.dataset.hoisted = 'true';
        document.body.appendChild(drawer);
      });
    },

    init() {
      this.overlay = $('[data-overlay]');

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-drawer-open]');
        if (opener) {
          const drawer = $(`[data-drawer="${opener.dataset.drawerOpen}"]`);
          if (drawer) {
            event.preventDefault();
            this.open(drawer, opener);
          }
          return;
        }
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.target === this.overlay) this.close();
      });

      document.addEventListener('keydown', (event) => {
        if (!this.active) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    },

    open(drawer, trigger) {
      if (this.active && this.active !== drawer) this.close(false);
      this.active = drawer;
      this.trigger = trigger || document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (trigger && trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
      if (this.overlay) this.overlay.classList.add('is-active');
      document.body.classList.add('is-locked');
      const focusTarget = drawer.querySelector('[data-predictive-input]') || drawer;
      window.setTimeout(() => focusTarget.focus({ preventScroll: true }), 80);
    },

    close(restoreFocus = true) {
      const drawer = this.active;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (this.trigger && this.trigger.hasAttribute && this.trigger.hasAttribute('aria-expanded')) {
        this.trigger.setAttribute('aria-expanded', 'false');
      }
      if (this.overlay) this.overlay.classList.remove('is-active');
      document.body.classList.remove('is-locked');
      if (restoreFocus && this.trigger && typeof this.trigger.focus === 'function') {
        this.trigger.focus({ preventScroll: true });
      }
      this.active = null;
    },

    trapFocus(event) {
      const focusables = $$(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])',
        this.active
      ).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  /* ==========================================================================
     Reveal on scroll
     ========================================================================== */
  const Reveal = {
    init(scope = document) {
      const elements = $$('[data-reveal]:not(.is-in)', scope);
      if (!document.body.classList.contains('has-animations') || !('IntersectionObserver' in window)) {
        elements.forEach((el) => el.classList.add('is-in'));
        return;
      }
      if (!this.observer) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-in');
                this.observer.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
        );
      }
      elements.forEach((el) => this.observer.observe(el));
    }
  };

  /* ==========================================================================
     Horizontal sliders (product rows, testimonials)
     ========================================================================== */
  function initSliders(scope = document) {
    $$('[data-slider]', scope).forEach((track) => {
      if (track.dataset.sliderReady) return;
      track.dataset.sliderReady = 'true';
      const controls = track.parentElement.querySelector('[data-slider-controls]');
      const progress = controls && controls.querySelector('[data-slider-progress]');
      const prev = controls && controls.querySelector('[data-slider-prev]');
      const next = controls && controls.querySelector('[data-slider-next]');

      const step = () => {
        const item = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
      };

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const visible = track.clientWidth / Math.max(track.scrollWidth, 1);
        const ratio = max > 0 ? track.scrollLeft / max : 1;
        if (progress) progress.style.setProperty('--progress', Math.min(1, visible + (1 - visible) * ratio).toFixed(3));
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        if (controls) controls.hidden = max <= 2;
      };

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', debounce(update, 20), { passive: true });
      window.addEventListener('resize', debounce(update, 120));
      update();
    });
  }

  /* ==========================================================================
     Footer wordmark: fit to container width
     ========================================================================== */
  function fitText() {
    $$('[data-fit-text]').forEach((el) => {
      const container = el.parentElement;
      el.style.fontSize = '100px';
      const width = el.scrollWidth;
      if (!width) return;
      const available = container.clientWidth - parseFloat(getComputedStyle(container).paddingLeft) * 2;
      el.style.fontSize = `${Math.floor((available / width) * 100)}px`;
    });
  }

  /* ==========================================================================
     Announcement bar
     ========================================================================== */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-announcement-item]', this);
      if (this.items.length < 2) return;
      this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      const nextBtn = this.querySelector('[data-announcement-next]');
      const prevBtn = this.querySelector('[data-announcement-prev]');
      if (nextBtn) nextBtn.addEventListener('click', () => this.go(this.index + 1, true));
      if (prevBtn) prevBtn.addEventListener('click', () => this.go(this.index - 1, true));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('shopify:block:select', (event) => {
        this.stop();
        this.go(this.items.indexOf(event.target));
      });
      this.addEventListener('shopify:block:deselect', () => this.start());
      this.start();
    }

    disconnectedCallback() { this.stop(); }

    start() {
      this.stop();
      this.timer = window.setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() { window.clearInterval(this.timer); }

    go(index, restart = false) {
      if (index < 0 || !this.items.length) index = this.items.length - 1;
      this.items[this.index].classList.remove('is-active');
      this.index = index % this.items.length;
      this.items[this.index].classList.add('is-active');
      if (restart) this.start();
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ==========================================================================
     Cart (AJAX + section rendering)
     ========================================================================== */
  const Cart = {
    sections: 'cart-drawer',

    async request(url, options) {
      const response = await fetch(url, options);
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      this.render(data.sections);
      return data;
    },

    add(formData) {
      formData.append('sections', this.sections);
      formData.append('sections_url', window.location.pathname);
      return this.request(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
    },

    change(line, quantity) {
      return this.request(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sections, sections_url: window.location.pathname })
      });
    },

    render(sections) {
      if (!sections || !sections['cart-drawer']) return;
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = doc.querySelector('[data-cart-inner]');
      const current = $('[data-cart-inner]');
      if (fresh && current) current.replaceWith(fresh);
      if (fresh) this.updateCount(parseInt(fresh.dataset.count, 10) || 0);
    },

    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        el.classList.remove('is-bump');
        void el.offsetWidth;
        el.classList.add('is-bump');
      });
    }
  };

  async function updateCartLine(lineEl, quantity) {
    if (!lineEl) return;
    lineEl.classList.add('is-updating');
    try {
      await Cart.change(parseInt(lineEl.dataset.cartLine, 10), quantity);
    } catch (error) {
      lineEl.classList.remove('is-updating');
      window.alert(error.message);
    }
  }

  const submitCartPage = debounce((form) => form.submit(), 450);

  document.addEventListener('click', (event) => {
    const qtyButton = event.target.closest('[data-qty-change]');
    if (qtyButton) {
      const input = qtyButton.closest('[data-qty]').querySelector('input');
      if (!input) return;
      const min = parseInt(input.min || '0', 10);
      input.value = Math.max(min, (parseInt(input.value, 10) || 0) + parseInt(qtyButton.dataset.qtyChange, 10));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const remove = event.target.closest('[data-line-remove]');
    if (remove && remove.closest('[data-cart-inner]')) {
      event.preventDefault();
      updateCartLine(remove.closest('[data-cart-line]'), 0);
    }
  });

  document.addEventListener('change', (event) => {
    const lineInput = event.target.closest('[data-line-quantity]');
    if (lineInput) {
      const quantity = Math.max(0, parseInt(lineInput.value, 10) || 0);
      if (lineInput.closest('[data-cart-inner]')) {
        updateCartLine(lineInput.closest('[data-cart-line]'), quantity);
      } else if (lineInput.form && lineInput.form.hasAttribute('data-cart-page-form')) {
        submitCartPage(lineInput.form);
      }
      return;
    }

    const sort = event.target.closest('[data-sort-select]');
    if (sort) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }
  });

  /* ==========================================================================
     Product page
     ========================================================================== */
  class ProductPage extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      try {
        this.product = json ? JSON.parse(json.textContent) : null;
      } catch (error) {
        this.product = null;
      }
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.idInput = this.querySelector('[data-variant-id-input]');
      this.initVariants();
      this.initGallery();
      this.initSwatches();
      this.initLightbox();
      this.initStickyBar();
      this.initForm();
      this.initShare();
    }

    /* ----- Variants ----- */
    initVariants() {
      this.picker = this.querySelector('[data-variant-picker]');
      if (!this.picker || !this.product) return;
      this.picker.addEventListener('change', () => this.onVariantChange());
    }

    selectedOptions() {
      return $$('.variant-picker__option', this.picker).map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        const select = fieldset.querySelector('select');
        if (checked) return checked.value;
        return select ? select.value : null;
      });
    }

    onVariantChange() {
      const options = this.selectedOptions();
      const variant = this.product.variants.find((v) => v.options.every((value, i) => value === options[i]));

      $$('.variant-picker__option', this.picker).forEach((fieldset, i) => {
        const label = fieldset.querySelector('[data-option-value]');
        if (label) label.textContent = options[i];
      });
      this.updateAvailability(options);

      if (!variant) {
        this.setButton(false, theme.strings.unavailable);
        return;
      }

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      this.updatePrice(variant);
      this.setButton(variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut);
      if (variant.featured_media) this.goToMedia(variant.featured_media.id);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updateAvailability(selected) {
      $$('.variant-picker__option', this.picker).forEach((fieldset, position) => {
        $$('input[type="radio"]', fieldset).forEach((input) => {
          const available = this.product.variants.some(
            (v) => v.available && v.options[position] === input.value && v.options.every((value, i) => i === position || value === selected[i])
          );
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updatePrice(variant) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const price = this.querySelector('.product__price [data-price]');
      if (price) {
        const current = price.querySelector('[data-price-current]');
        const compare = price.querySelector('[data-price-compare]');
        const badge = price.querySelector('[data-price-badge]');
        price.classList.toggle('price--sale', Boolean(onSale));
        if (current) current.innerHTML = formatMoney(variant.price);
        if (compare) {
          compare.hidden = !onSale;
          if (onSale) compare.innerHTML = `<span class="visually-hidden">${theme.strings.regularPrice || ''}</span>${formatMoney(variant.compare_at_price)}`;
        }
        if (badge) {
          badge.hidden = !onSale;
          if (onSale) badge.textContent = `-${Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)}%`;
        }
      }
      const sticky = this.querySelector('[data-sticky-price]');
      if (sticky) sticky.innerHTML = formatMoney(variant.price);
    }

    setButton(enabled, label) {
      const button = this.querySelector('[data-add-to-cart]');
      if (!button) return;
      button.disabled = !enabled;
      const text = button.querySelector('[data-add-to-cart-text]');
      if (text) text.textContent = label;
    }

    /* ----- Gallery ----- */
    initGallery() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.items = $$('.product-gallery__item', this.track);
      const indexEl = this.querySelector('[data-gallery-index]');
      const progressEl = this.querySelector('[data-gallery-progress]');

      const update = () => {
        if (this.track.scrollWidth <= this.track.clientWidth + 2) return;
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (indexEl) indexEl.textContent = Math.min(this.items.length, index + 1);
        if (progressEl) progressEl.style.setProperty('--progress', ((index + 1) / this.items.length).toFixed(3));
      };

      this.track.addEventListener('scroll', debounce(update, 30), { passive: true });
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.track.scrollBy({ left: -this.track.clientWidth, behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => this.track.scrollBy({ left: this.track.clientWidth, behavior: 'smooth' }));
    }

    /* ----- Pastilles de coloris ----- */
    // Un clic : la photo du coloris s'affiche tout de suite (sans attendre la taille) et la
    // même couleur est cochée dans le formulaire COD. L'inverse marche aussi.
    initSwatches() {
      const box = this.querySelector('[data-color-swatches]');
      if (!box || !this.product) return;
      const pos = Number(box.dataset.position);
      const buttons = $$('[data-swatch]', box);
      const name = box.querySelector('[data-swatch-name]');
      const codField = this.querySelector(`[data-cod-option="${pos}"]`);
      const pickerField = this.picker && this.picker.querySelector(`[data-option-position="${pos + 1}"]`);

      const check = (field, value) => {
        if (!field) return;
        const input = $$('input[type="radio"]', field).find((i) => i.value === value);
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };

      const pick = (value, origin) => {
        buttons.forEach((b) => b.setAttribute('aria-checked', String(b.dataset.swatch === value)));
        if (name) name.textContent = value;
        const variant = this.product.variants.find((v) => v.options[pos] === value && v.featured_media);
        if (variant && origin !== 'init') {
          // Seule une pastille du haut ramène à la galerie : un choix fait dans le formulaire
          // ne doit jamais faire défiler la page (on perdait la cliente en pleine saisie).
          this.goToMedia(variant.featured_media.id, true, origin === 'swatch');
          const thumb = this.querySelector('.sticky-buy__img');
          const src = variant.featured_media.preview_image && variant.featured_media.preview_image.src;
          if (thumb && src) {
            thumb.removeAttribute('srcset');
            thumb.src = src + (src.includes('?') ? '&' : '?') + 'width=160';
          }
        }
        if (origin !== 'cod') check(codField, value);
        if (origin !== 'picker') check(pickerField, value);
      };

      buttons.forEach((b) => b.addEventListener('click', () => pick(b.dataset.swatch, 'swatch')));
      if (codField) codField.addEventListener('change', (e) => { if (e.target.type === 'radio') pick(e.target.value, 'cod'); });
      if (pickerField) pickerField.addEventListener('change', (e) => { if (e.target.type === 'radio') pick(e.target.value, 'picker'); });

      const current = buttons.find((b) => b.getAttribute('aria-checked') === 'true');
      if (current) pick(current.dataset.swatch, 'init');
    }

    goToMedia(mediaId, fade, remonter) {
      if (!this.track) return;
      const item = this.track.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      if (fade) {
        item.classList.remove('is-swap');
        void item.offsetWidth;
        item.classList.add('is-swap');
      }
      if (this.track.scrollWidth > this.track.clientWidth + 2) {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      } else if (item !== this.track.firstElementChild) {
        this.track.prepend(item);
      }
      // Sur ordinateur la photo passe en tête de galerie : si on avait défilé, on y remonte.
      if (fade && remonter && this.track.getBoundingClientRect().top < 0) {
        window.scrollTo({ top: window.scrollY + this.track.getBoundingClientRect().top - 90, behavior: 'smooth' });
      }
    }

    /* ----- Lightbox ----- */
    initLightbox() {
      this.lightbox = this.querySelector('[data-lightbox]');
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-zoom-open]');
        if (!trigger || trigger.disabled) return;
        this.lightbox.showModal();
        document.body.classList.add('is-locked');
        const target = this.lightbox.querySelector(`[data-lightbox-item="${trigger.dataset.zoomOpen}"]`);
        window.requestAnimationFrame(() => target && target.scrollIntoView({ block: 'start' }));
      });

      this.lightbox.addEventListener('click', (event) => {
        if (event.target.closest('[data-lightbox-close]') || event.target.classList.contains('lightbox__img')) {
          this.lightbox.close();
        }
      });
      this.lightbox.addEventListener('close', () => document.body.classList.remove('is-locked'));
    }

    /* ----- Sticky mobile buy bar ----- */
    initStickyBar() {
      this.stickyBar = this.querySelector('[data-sticky-buy]');
      this.buyAnchor = this.querySelector('[data-buy-anchor]') || this.querySelector('.product__info-container');
      if (!this.stickyBar || !this.buyAnchor) return;

      const button = this.stickyBar.querySelector('[data-sticky-buy-button]');
      const toggle = (show) => {
        if (this.stickyBar.classList.contains('is-visible') === show) return;
        this.stickyBar.classList.toggle('is-visible', show);
        this.stickyBar.setAttribute('aria-hidden', String(!show));
        if (button) button.tabIndex = show ? 0 : -1;
      };

      // Visible dès qu'on s'éloigne du formulaire — avant (pendant qu'on regarde les photos)
      // comme après — et masquée tant qu'il est à l'écran : même règle que la landing.
      let pending = false;
      const update = () => {
        pending = false;
        const r = this.buyZone().getBoundingClientRect();
        const inView = r.top < window.innerHeight * 0.9 && r.bottom > 80;
        toggle(!inView && (r.bottom <= 80 || window.scrollY > 250));
      };
      window.addEventListener('scroll', () => {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(update);
      }, { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();

      if (button) {
        button.addEventListener('click', () => {
          // Formulaire COD encore replié : la barre l'ouvre directement (cod.js fait le défilement).
          const cod = this.querySelector('cod-form');
          const etape = cod && cod.querySelector('[data-cod-etape]');
          if (etape && etape.hidden && cod.ouvrir) { cod.ouvrir(); return; }
          const zone = this.buyZone();
          const offset = parseFloat(getComputedStyle(root).getPropertyValue('--header-total')) || 0;
          window.scrollTo({ top: zone.getBoundingClientRect().top + window.scrollY - offset - 16, behavior: 'smooth' });
        });
      }
    }

    buyZone() {
      const appForm = this.querySelector('.product__info-container [class*="leadform"], .product__info-container [id*="leadform"], .product__info-container [class*="lf-form"]');
      return appForm || this.buyAnchor;
    }

    /* ----- Add to cart ----- */
    initForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', async (event) => {
        if (theme.cartType !== 'drawer' || !$('#CartDrawer')) return;
        event.preventDefault();
        const button = this.form.querySelector('[data-add-to-cart]');
        const error = this.form.querySelector('[data-form-error]');
        if (button && button.disabled) return;
        if (button) button.classList.add('is-loading');
        if (error) error.hidden = true;
        try {
          await Cart.add(new FormData(this.form));
          Drawer.open($('#CartDrawer'), button);
        } catch (err) {
          if (error) {
            error.textContent = err.message;
            error.hidden = false;
          }
        } finally {
          if (button) button.classList.remove('is-loading');
        }
      });
    }

    /* ----- Share ----- */
    initShare() {
      const button = this.querySelector('[data-share]');
      if (!button) return;
      button.addEventListener('click', async () => {
        const data = { title: button.dataset.shareTitle, url: button.dataset.shareUrl };
        if (navigator.share) {
          try { await navigator.share(data); } catch (error) { /* dismissed */ }
          return;
        }
        try {
          await navigator.clipboard.writeText(data.url);
          const label = button.querySelector('[data-share-label]');
          const original = label.textContent;
          label.textContent = theme.strings.copied;
          window.setTimeout(() => { label.textContent = original; }, 2000);
        } catch (error) { /* clipboard unavailable */ }
      });
    }
  }
  customElements.define('product-page', ProductPage);

  /* ==========================================================================
     Product recommendations
     ========================================================================== */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.product-grid') || !this.dataset.url) return;
      const fallback = this.querySelector('template[data-recommendations-fallback]');
      const render = (fragment) => {
        this.innerHTML = '';
        this.appendChild(fragment);
        Reveal.init(this);
        initSliders(this);
      };
      const useFallback = () => {
        if (fallback && fallback.content.querySelector('.card')) {
          render(fallback.content.cloneNode(true));
        } else {
          this.hidden = true;
        }
      };
      const load = async () => {
        try {
          const doc = await fetchHTML(this.dataset.url);
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.querySelector('.card')) {
            const fragment = document.createDocumentFragment();
            Array.from(fresh.childNodes).forEach((node) => fragment.appendChild(node));
            render(fragment);
          } else {
            useFallback();
          }
        } catch (error) {
          useFallback();
        }
      };
      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        load();
      }, { rootMargin: '0px 0px 500px 0px' });
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ==========================================================================
     Predictive search
     ========================================================================== */
  function initPredictiveSearch() {
    const input = $('[data-predictive-input]');
    const results = $('[data-predictive-results]');
    if (!input || !results || input.dataset.ready) return;
    input.dataset.ready = 'true';
    const defaults = $('[data-predictive-default]');
    let controller;

    input.addEventListener('input', debounce(async () => {
      const terms = input.value.trim();
      if (!terms) {
        results.innerHTML = '';
        if (defaults) defaults.classList.remove('is-hidden');
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q: terms,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[options][prefix]': 'last'
      });
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: controller.signal });
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = doc.querySelector('[data-predictive-content]');
        results.innerHTML = content ? content.outerHTML : '';
        if (defaults) defaults.classList.add('is-hidden');
      } catch (error) {
        if (error.name !== 'AbortError') results.innerHTML = '';
      }
    }, 220));
  }

  /* ==========================================================================
     Collection: load more, grid density
     ========================================================================== */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-load-more]');
    if (!button) return;
    event.preventDefault();
    button.classList.add('is-loading');
    try {
      const doc = await fetchHTML(button.href);
      const grid = $('[data-grid]');
      const incoming = doc.querySelector('[data-grid]');
      if (grid && incoming) {
        Array.from(incoming.children).forEach((child) => grid.appendChild(child));
        Reveal.init(grid);
      }
      const wrap = button.closest('[data-load-more-wrap]');
      const freshWrap = doc.querySelector('[data-load-more-wrap]');
      if (wrap && freshWrap) {
        const previous = freshWrap.querySelector('.load-more__prev');
        if (previous) previous.remove();
        wrap.replaceWith(freshWrap);
      } else if (wrap) {
        wrap.remove();
      }
    } catch (error) {
      window.location.href = button.href;
    }
  });

  function initGridToggle() {
    const grid = $('[data-grid]');
    const buttons = $$('[data-grid-cols]');
    if (!grid || !buttons.length) return;
    const set = (mode, persist) => {
      grid.dataset.cols = mode;
      buttons.forEach((b) => b.classList.toggle('is-active', b.dataset.gridCols === mode));
      if (persist) {
        try { window.localStorage.setItem('milos-grid', mode); } catch (error) { /* storage unavailable */ }
      }
    };
    let saved = null;
    try { saved = window.localStorage.getItem('milos-grid'); } catch (error) { /* storage unavailable */ }
    set(saved === 'large' ? 'large' : 'small', false);
    buttons.forEach((b) => b.addEventListener('click', () => set(b.dataset.gridCols, true)));
  }

  /* ==========================================================================
     Boot
     ========================================================================== */
  function boot() {
    Drawer.hoist();
    Drawer.init();
    Header.refresh();
    Reveal.init();
    initSliders();
    initPredictiveSearch();
    initGridToggle();
    fitText();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitText);
    window.addEventListener('resize', debounce(fitText, 150));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-header]')) {
      Drawer.hoist();
      Header.refresh();
      initPredictiveSearch();
    }
    Reveal.init(event.target);
    initSliders(event.target);
    initGridToggle();
    fitText();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('[data-header]')) {
      $$('body > [data-hoisted="true"]').forEach((drawer) => drawer.remove());
    }
  });

  window.MilosTheme = { Cart, Drawer, formatMoney };
})();
